import "./../styles.scss";

import { FolderOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { get } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";

import AccessControl from "../../../components/common/AccessControl/accessControl";
import { ClientWebhook, RVMP, UploadDropzone } from "../../../components/icons";
import DatafeedIcon from "../../../components/icons/DatafeedIcon";
import HistoryDark from "../../../components/icons/HistoryDark";
import Security from "../../../components/icons/Security";
import VCategory from "../../../components/icons/VCategory";
import VEvents from "../../../components/icons/VEvents";
import VideoFilm from "../../../components/icons/VideoFilm";
import VPlayer from "../../../components/icons/VPlayer";
import VStorage from "../../../components/icons/VStorage";
import VTeam from "../../../components/icons/VTeam";
import VTournament from "../../../components/icons/VTournament";
import { allUserRoles, ENTITY_TYPE } from "../../../constants/phase";
import amplitude from "../../../externals/amplitude";
import { getEntityType } from "../../../helpers";

const SettingSideBar = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, currentRole } = useOutletContext();
  const { currentItem, parentClientSetting, clientSetting, setCurrentItem, active_tab, getClientSetting } = props;
  const [_orgClientSettings, setOrgClientSettings] = useState();

  const baseConfigurationOptions = useMemo(
    () => [
      { name: "Category", dbKey: "category", key: "category", accessKey: "view_category", item: <VCategory /> },
      {
        name: "Custom Fields",
        dbKey: "customFields",
        key: "customFields",
        accessKey: "view_customFields",
        item: <VideoFilm />,
      },
      { name: "Folders", dbKey: "Folders", key: "folder", accessKey: "view_folder", item: <FolderOutlined /> },
      { name: "Event", dbKey: "Event", key: "event", accessKey: "view_event", item: <VEvents /> },
      { name: "Player", key: "player", dbKey: "Player", accessKey: "view_player", item: <VPlayer /> },
      { name: "Team", dbKey: "Team", key: "team", accessKey: "view_team", item: <VTeam /> },
      {
        name: "Tournament",
        dbKey: "Tournament",
        key: "tournament",
        accessKey: "view_tournament",
        item: <VTournament />,
      },
      { name: "RTMP Channel", dbKey: "RTMP", key: "rtmp", accessKey: "view_channel", item: <RVMP /> },
      { name: "Storage", key: "storage", dbKey: "addStorages", accessKey: "view_storage", item: <VStorage /> },
      { name: "Shows", dbKey: "Show", key: "show", accessKey: "view_show", item: <RVMP /> },
      { name: "Datafeed", key: "datafeed", dbKey: "datafeed", accessKey: "view_datafeed", item: <DatafeedIcon /> },
      {
        name: "Client Webhook",
        key: "client-webhook",
        dbKey: "clientWebHook",
        accessKey: "view_client_webhook",
        item: <ClientWebhook className="dark__icon" />,
      },
      {
        name: "S3 Glacier Restore",
        key: "s3_glacier_restore",
        dbKey: "upload_stream_csv",
        accessKey: "upload_stream_csv",
        item: <UploadDropzone />,
      },
      {
        name: "YouTube download",
        key: "upload_youtube_csv",
        dbKey: "upload_youtube_csv",
        accessKey: "upload_youtube_csv",
        item: <UploadDropzone />,
      },
    ],
    [],
  );

  const allConfigurationOptions = useMemo(() => {
    const options = [...baseConfigurationOptions];
    if (currentRole !== allUserRoles.superAdmin) {
      options.push({
        name: "Reserved Channel",
        key: "reserved-channel",
        dbKey: "reserved_channel",
        accessKey: "reserved_channel",
        item: <UploadDropzone />,
      });
    }
    if (
      currentRole !== allUserRoles.superAdmin &&
      user.hasOwnProperty("clientId") &&
      currentRole === allUserRoles.admin
    ) {
      options.push({
        name: "Sub client",
        key: "subclients",
        dbKey: "subclients",
        accessKey: "add_sub_clients",
        item: <UsergroupAddOutlined />,
      });
    }
    if (clientSetting?.permissions?.zeepublishing) {
      options.push({
        name: "Package",
        key: "package",
        dbKey: "showSettings",
        accessKey: "view_packages",
        item: <VCategory />,
      });
    }
    if (clientSetting?.permissions?.outputVideoTemplate) {
      options.push({
        name: "Video Template",
        key: "output_video_template",
        dbKey: "output_video_template",
        accessKey: "output_video_template",
        item: <HistoryDark className="dark__icon" />,
      });
    }
    if (currentRole === allUserRoles.superAdmin) {
      options.push({
        name: "SRT encryption",
        key: "srt_encryption",
        dbKey: "srt_encryption",
        accessKey: "srt_encryption",
        item: <Security />,
      });
    }
    return options;
  }, [
    baseConfigurationOptions,
    currentRole,
    user,
    clientSetting?.permissions?.zeepublishing,
    clientSetting?.permissions?.outputVideoTemplate,
  ]);

  const configurationOptions = useMemo(() => {
    if (!currentRole) return [];
    return allConfigurationOptions.filter((data) => {
      if (
        currentRole === allUserRoles.superAdmin ||
        currentRole === allUserRoles.admin ||
        currentRole === allUserRoles.user ||
        currentRole === allUserRoles.reviewer
      ) {
        return true;
      }
      if (
        parentClientSetting?.permissions?.reviewerFunctionality &&
        get(clientSetting, `permissions.${data.dbKey}`, false) &&
        currentRole === allUserRoles.user
      ) {
        return true;
      }
      return false;
    });
  }, [allConfigurationOptions, currentRole, parentClientSetting?.permissions?.reviewerFunctionality, clientSetting]);

  const getOrganizationPermissions = useCallback(async () => {
    try {
      const { value } = await getClientSetting({ entityId: id });
      setOrgClientSettings(value?.data);
    } catch (error) {
      console.log(error);
    }
  }, [id, getClientSetting]);

  useEffect(() => {
    getOrganizationPermissions();
  }, [getOrganizationPermissions]);

  useEffect(() => {
    if (configurationOptions?.length) {
      setCurrentItem({ name: configurationOptions[0].name, key: configurationOptions[0].key });
    }
  }, [configurationOptions, setCurrentItem]);

  const onLinkChange = (item) => {
    setCurrentItem({ name: item.name, key: item.key });
    if (currentItem?.key !== item.key) {
      navigate(
        `/${ENTITY_TYPE.ORGANIZATION === getEntityType(id) ? "organizations" : "workspaces"}-profile/${id}/${item?.key}`,
        { replace: true },
      );
    }
  };
  return (
    <>
      {/* //  ["admin", "user"].includes(userData?.role) &&
                  //   !["server", "story-template"].includes(item.key) ? ( */}

      {configurationOptions?.length > 0 && (
        <ul>
          {configurationOptions.map((item) => {
            return (
              <AccessControl code={item.accessKey} page="organization-profile">
                <li>
                  <div>
                    <Link
                      to={`/${ENTITY_TYPE.ORGANIZATION === getEntityType(id) ? "organizations" : "workspaces"}-profile/${id}/${item?.key}`}
                      onClick={() => {
                        onLinkChange(item);
                        amplitude.logEvent("configuration_sidebar_clicked", { item: item?.key });
                      }}
                      className={active_tab === item.key ? "active" : ""}
                      replace
                    >
                      {item.item}
                      {item.name}
                    </Link>
                  </div>
                </li>
              </AccessControl>
            );
          })}
        </ul>
      )}
    </>
  );
};

export default SettingSideBar;
